import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BrandXS, LabelS } from '@sorare/core/src/atoms/typography';
import { LightedBezel } from '@sorare/core/src/atoms/ui/bezels/LightedBezel';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import type { HexColor } from '@sorare/core/src/style/types';

import { ClaimButton } from '#src/components/missions/UnclaimedTask/ClaimButton/index.tsx';

import type {
  ProgressTaskCard_anyRewardConfigInterface,
  ProgressTaskCard_taskInterface,
} from './__generated__/index.graphql.ts';

const Container = styled(LinkBox)`
  width: 100%;
  position: relative;
  &.claimed {
    opacity: 0.5;
  }
  &.selected {
    opacity: 1;
  }
  transition: all 0.2s ease-in-out;
`;

const CardContent = styled(Horizontal).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  position: relative;
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const StyledLightedBezel = styled(LightedBezel)`
  width: 100%;
`;

const StyledLinkOverlay = styled(LinkOverlay)`
  width: 100%;
  text-align: left;
  cursor: pointer;
`;

const InProgressTaskWrapper = styled(LinkBox)`
  width: 100%;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);
  background: var(--c-nl-800);
  text-align: left;
  position: relative;
  transition: all 0.2s ease-in-out;
  &.selected {
    background: var(--c-nd-50);
    border: 1px solid var(--c-white);
  }
`;

const ProgressSection = styled(Horizontal).attrs({ gap: 1 })`
  flex: 1;
  align-items: center;
  min-width: 0;
`;

const StyledProgress = styled(Progress)`
  flex: 1;
  min-width: 0;
`;

const ContentWrapper = styled(Vertical)`
  @media ${laptopAndAbove} {
    align-items: flex-start;
  }
`;

type Props = {
  task: ProgressTaskCard_taskInterface;
  category: ReactNode;
  description: ReactNode;
  accentColor: HexColor;
  accentColorRgb?: string;
  titleColor: HexColor;
  progress: number;
  target: number;
  rewardConfig?: ProgressTaskCard_anyRewardConfigInterface | null;
  progressTrailing?: ReactNode;
  onClaim: () => void;
  loading: boolean;
  onClick?: (() => void) | null;
  selected?: boolean;
};

export const ProgressTaskCard = ({
  task,
  category,
  description,
  accentColor,
  accentColorRgb,
  titleColor,
  progress,
  target,
  rewardConfig,
  progressTrailing,
  onClaim,
  loading,
  onClick,
  selected,
}: Props) => {
  const { aasmState } = task;
  const isCompleted = aasmState === ManagerProgressionTaskState.COMPLETED;
  const isClaimed = aasmState === ManagerProgressionTaskState.CLAIMED;

  if (!isCompleted && !isClaimed) {
    return (
      <InProgressTaskWrapper className={classNames({ selected })}>
        <Vertical>
          <Vertical>
            <BrandXS bold color={titleColor}>
              {category}
            </BrandXS>
            <StyledLinkOverlay
              as="button"
              onClick={onClick || undefined}
              disabled={!onClick}
            >
              {description}
            </StyledLinkOverlay>
          </Vertical>
          <SBHorizontal gap={2}>
            <ProgressSection>
              <StyledProgress
                value={progress}
                max={target}
                color={accentColor}
                bgColor="var(--c-nd-150-opaque)"
              />
              <Horizontal>
                <LabelS bold>{progress}</LabelS>
                <LabelS color="var(--c-nd-600)">/</LabelS>
                <LabelS color="var(--c-nd-600)" bold>
                  {target}
                </LabelS>
                {progressTrailing}
              </Horizontal>
            </ProgressSection>
            {!!rewardConfig && (
              <AnyRewardConfig
                anyRewardConfig={rewardConfig}
                variant={Variant.INLINE_CHIP}
              />
            )}
          </SBHorizontal>
        </Vertical>
      </InProgressTaskWrapper>
    );
  }

  return (
    <Container className={classNames({ claimed: isClaimed, selected })}>
      <StyledLightedBezel
        color={accentColor}
        bezelSize={16}
        radius={16}
        borderWidth={1}
        background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, rgba(var(--c-rgb-black), 0.2) 0) 0 0/5px 5px"
        boxShadow={
          selected
            ? `0px 0px 8px 2px ${accentColorRgb ? `rgba(${accentColorRgb}, 0.2)` : 'var(--c-nd-50)'} inset`
            : undefined
        }
      >
        <CardContent>
          {!!rewardConfig && (
            <AnyRewardConfig
              anyRewardConfig={rewardConfig}
              variant={Variant.CHIP}
            />
          )}
          <ContentWrapper>
            <BrandXS bold color={titleColor}>
              {category}
            </BrandXS>
            <StyledLinkOverlay
              as="button"
              onClick={onClick || undefined}
              disabled={!onClick}
            >
              {description}
            </StyledLinkOverlay>
            {!!isCompleted && (
              <LinkOther as="div">
                <ClaimButton task={task} onClaim={onClaim} loading={loading} />
              </LinkOther>
            )}
          </ContentWrapper>
        </CardContent>
      </StyledLightedBezel>
    </Container>
  );
};

ProgressTaskCard.fragments = {
  taskInterface: gql`
    fragment ProgressTaskCard_taskInterface on TaskInterface {
      id
      aasmState
      ...ClaimButton_taskInterface
    }
    ${ClaimButton.fragments.taskInterface}
  ` as TypedDocumentNode<ProgressTaskCard_taskInterface>,
  anyRewardConfigInterface: gql`
    fragment ProgressTaskCard_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...AnyRewardConfig_anyRewardConfigInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ProgressTaskCard_anyRewardConfigInterface>,
};
